/*  ==============================================================
    Dre.jsx  (DRE Operacional) – versão atualizada
    ----------------------------------------------------------------
    • Variáveis exibidas na tabela contábil apenas como texto (R$)
    • Edição de valor da variável ocorre dentro do Modal ⚙️
    • Campo 'Valor' oculto ao criar nova variável (inicia em 0)
   ============================================================== */

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

/* ----------------- CONFIGURAÇÃO ----------------- */
const API_BASE_URL = "https://api-vizum-01.lumixoticas.com.br"; // <‑ ajuste se necessário

/* ----------------- HELPERS ----------------- */
const formatCurrency = (val) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(val || 0);

const MESES = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

/* ==============================================================
   COMPONENT
   ============================================================== */
export default function Dre() {
  /* ----------------- ESTADOS ----------------- */
  const [ordens, setOrdens] = useState([]);
  const [despesasDB, setDespesasDB] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // ---- filtros principais (ano / loja) ----
  const [anoSelecionado, setAnoSelecionado] = useState(
    new Date().getFullYear().toString()
  );
  const [filtroLoja, setFiltroLoja] = useState("Vizum 01"); // padrão

  // ---- intervalo de datas (usado nos cálculos) ----
  const [dateInicio, setDateInicio] = useState("");
  const [dateFim, setDateFim] = useState("");
  const [activePreset, setActivePreset] = useState(null);

  // ---- modal DRE (lançamento de despesas) ----
  const [modalAberto, setModalAberto] = useState(false);
  const [dadosMesEdit, setDadosMesEdit] = useState(null);

  // ---- variáveis customizadas -----------------
  const [variaveis, setVariaveis] = useState([]);
  const [variavelEdit, setVariavelEdit] = useState(null);
  const [modalVariavelAberto, setModalVariavelAberto] = useState(false);

  // ---- valores de variáveis por mês ----
  const [variaveisMesValores, setVariaveisMesValores] = useState({});

  /* ----------------- INICIALIZAÇÃO DO INTERVALO ----------------- */
  useEffect(() => {
    const start = new Date(`${anoSelecionado}-01-01`);
    const end = new Date(`${anoSelecionado}-12-31`);
    setDateInicio(start.toISOString().split("T")[0]);
    setDateFim(end.toISOString().split("T")[0]);
    setActivePreset(null);
  }, [anoSelecionado]);

  // ==== PRESET FILTERS ====
  const applyPreset = useCallback(
    (type) => {
      const now = new Date();
      const yearStart = new Date(`${anoSelecionado}-01-01`);
      const yearEnd = new Date(`${anoSelecionado}-12-31`);

      let start, end;

      switch (type) {
        case "today":
          start = end = now;
          break;
        case "week": {
          const day = now.getDay();
          const diffToMon = day === 0 ? -6 : 1 - day;
          start = new Date(now);
          start.setDate(now.getDate() + diffToMon);
          end = now;
          break;
        }
        case "month":
          start = new Date(now.getFullYear(), now.getMonth(), 1);
          end = now;
          break;
        case "lastMonth":
          start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          end = new Date(now.getFullYear(), now.getMonth(), 0);
          break;
        case "last7":
          start = new Date(now);
          start.setDate(now.getDate() - 6);
          end = now;
          break;
        case "last30":
          start = new Date(now);
          start.setDate(now.getDate() - 29);
          end = now;
          break;
        default:
          start = yearStart;
          end = yearEnd;
      }

      if (start < yearStart) start = yearStart;
      if (end > yearEnd) end = yearEnd;

      setDateInicio(start.toISOString().split("T")[0]);
      setDateFim(end.toISOString().split("T")[0]);
      setActivePreset(type);
    },
    [anoSelecionado]
  );

  /* ----------------- BUSCA DE DADOS ----------------- */
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("vizumToken");
        const headers = { Authorization: `Bearer ${token}` };

        const [resOS, resDRE] = await Promise.all([
          fetch(`${API_BASE_URL}/ordens-servico`, { headers }),
          fetch(
            `${API_BASE_URL}/dre?ano=${anoSelecionado}&loja=${filtroLoja}`,
            { headers }
          ),
        ]);

        const dataOS = await resOS.json();
        const dataDRE = await resDRE.json();

        setOrdens(Array.isArray(dataOS) ? dataOS : []);
        setDespesasDB(Array.isArray(dataDRE) ? dataDRE : []);
      } catch (err) {
        console.error("Erro ao buscar dados:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [anoSelecionado, filtroLoja, refreshTrigger]);

  useEffect(() => {
    const fetchVariaveis = async () => {
      const token = localStorage.getItem("vizumToken");
      const qs = new URLSearchParams({
        year: anoSelecionado,
        store: filtroLoja,
      });
      const res = await fetch(`${API_BASE_URL}/variables?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setVariaveis(data);

      const valores = {};
      data.forEach((v) => {
        if (v.isGlobal) {
          valores[v._id] = {};
          for (let i = 1; i <= 12; i++) valores[v._id][i] = v.value;
        } else if (v.month) {
          valores[v._id] = { [v.month]: v.value };
        }
      });
      setVariaveisMesValores(valores);
    };
    fetchVariaveis();
  }, [anoSelecionado, filtroLoja, refreshTrigger]);

  /* ----------------- CÁLCULO FINANCEIRO ----------------- */
  const dreData = useMemo(() => {
    const mesesArray = Array.from({ length: 12 }, (_, i) => ({
      mes: MESES[i],
      mesIndex: i,
      receitaBruta: 0,
      impostos: 0,
      receitaLiquida: 0,
      custoArmacoes: 0,
      custoLentesLab: 0,
      custoOutros: 0,
      lucroBruto: 0,
      comissoes: 0,
      taxasCartao: 0,
      folhaPagamento: 0,
      aluguel: 0,
      marketing: 0,
      despesasGerais: 0,
      totalVariaveis: 0,
      lucroLiquido: 0,
      margemLucro: 0,
    }));

    const kpiAno = {
      receitaTotal: 0,
      custoProdutos: 0,
      despesasTotais: 0,
      lucroTotal: 0,
    };

    /* 1️⃣ RECEITAS (ordens‑serviço) */
    ordens.forEach((os) => {
      if (filtroLoja !== "Todas" && os.loja !== filtroLoja) return;

      const dataStr = os.createdAt || os.data || os.dataEntrada;
      if (!dataStr) return;

      const dataOS = new Date(dataStr);
      if (dataOS.getFullYear() !== Number(anoSelecionado)) return;

      const iso = dataOS.toISOString().split("T")[0];
      if (iso < dateInicio || iso > dateFim) return;

      const mesIdx = dataOS.getMonth();
      const valorOS =
        Number(os.valorTotal) ||
        (os.pagamentos || []).reduce((a, p) => a + (p.valor ?? 0), 0);

      mesesArray[mesIdx].receitaBruta += valorOS;
    });

    /* 2️⃣ DESPESAS (tabela DRE) */
    mesesArray.forEach((m) => {
      const dreMesBanco = despesasDB.find((d) => d.mes === m.mesIndex + 1);
      if (dreMesBanco?.financeiro) {
        const f = dreMesBanco.financeiro;
        m.impostos = f.impostos || 0;
        m.custoArmacoes = f.custoArmacoes || 0;
        m.custoLentesLab = f.custoLentesLab || 0;
        m.custoOutros = f.custoOutros || 0;
        m.comissoes = f.comissoes || 0;
        m.taxasCartao = f.taxasCartao || 0;
        m.folhaPagamento = f.folhaPagamento || 0;
        m.aluguel = f.aluguel || 0;
        m.marketing = f.marketing || 0;
        m.despesasGerais = f.despesasGerais || 0;
      }

      /* 3️⃣ VARIÁVEIS GLOBAIS (separa em totalVariaveis) */
      m.totalVariaveis = 0;
      variaveis
        .filter((v) => v.isGlobal && v.year === Number(anoSelecionado) && v.store === filtroLoja)
        .forEach((v) => {
          const valorDoMes = variaveisMesValores[v._id]?.[m.mesIndex + 1];
          const valor = valorDoMes != null ? Number(valorDoMes) : v.value;
          m.totalVariaveis += valor;
        });

      /* 4️⃣ CÁLCULO FINANCEIRO */
      m.receitaLiquida = m.receitaBruta - m.impostos;

      const totalCPV = m.custoArmacoes + m.custoLentesLab + m.custoOutros;
      m.lucroBruto = m.receitaLiquida - totalCPV;

      const totalDespesas =
        m.comissoes +
        m.taxasCartao +
        m.folhaPagamento +
        m.aluguel +
        m.marketing +
        m.despesasGerais +
        m.totalVariaveis;

      m.lucroLiquido = m.lucroBruto - totalDespesas;
      m.margemLucro =
        m.receitaBruta > 0 ? (m.lucroLiquido / m.receitaBruta) * 100 : 0;

      /* 5️⃣ ACUMULAÇÃO PARA KPI ANUAL */
      kpiAno.receitaTotal += m.receitaBruta;
      kpiAno.custoProdutos += totalCPV;
      kpiAno.despesasTotais += totalDespesas;
      kpiAno.lucroTotal += m.lucroLiquido;
    });

    return { meses: mesesArray, kpiAno };
  }, [
    ordens,
    despesasDB,
    anoSelecionado,
    filtroLoja,
    dateInicio,
    dateFim,
    variaveis,
    variaveisMesValores,
  ]);

  const { meses, kpiAno } = dreData;
  const margemAnual =
    kpiAno.receitaTotal > 0
      ? (kpiAno.lucroTotal / kpiAno.receitaTotal) * 100
      : 0;

  const totalEligibleContracts = useMemo(() => {
    let cnt = 0;
    ordens.forEach((os) => {
      if (filtroLoja !== "Todas" && os.loja !== filtroLoja) return;

      const dataStr = os.createdAt || os.data || os.dataEntrada;
      if (!dataStr) return;

      const d = new Date(dataStr);
      if (d.getFullYear() !== Number(anoSelecionado)) return;

      const iso = d.toISOString().split("T")[0];
      if (iso < dateInicio || iso > dateFim) return;

      const valorOS =
        Number(os.valorTotal) ||
        (os.pagamentos || []).reduce((a, p) => a + (p.valor ?? 0), 0);

      if (valorOS > 0) cnt++;
    });
    return cnt;
  }, [ordens, filtroLoja, anoSelecionado, dateInicio, dateFim]);

  /* ----------------- INTERAÇÕES (MODAL DRE) ----------------- */
  const abrirDetalhesMes = (mesData) => {
    if (filtroLoja === "Todas") {
      alert(
        "🔒 Selecione uma Unidade específica (ex.: Vizum 01) no topo da tela para poder fazer lançamentos financeiros."
      );
      return;
    }

    // Carrega os valores das variáveis deste mês para jogar no Modal
    const variaveisGlobais = variaveis.filter(
      (v) => v.isGlobal && v.year === Number(anoSelecionado) && v.store === filtroLoja
    );

    const variaveisInputs = {};
    const variaveisInputsRaw = {}; // para digitação no input

    variaveisGlobais.forEach((v) => {
      const val = variaveisMesValores[v._id]?.[mesData.mesIndex + 1] ?? v.value;
      variaveisInputs[v._id] = val;
      variaveisInputsRaw[v._id] = val.toString();
    });

    setDadosMesEdit({
      ...mesData,
      variaveisInputs,
      variaveisInputsRaw,
    });
    setModalAberto(true);
  };

  // Input de despesas padrão no modal
  const handleInputChange = (campo, valor) => {
    const num = valor === "" ? 0 : parseFloat(valor);
    setDadosMesEdit((prev) => {
      const atualizado = {
        ...prev,
        [campo]: num,
        [`${campo}Input`]: valor,
      };

      atualizado.receitaLiquida = atualizado.receitaBruta - atualizado.impostos;

      const totalCPV =
        atualizado.custoArmacoes +
        atualizado.custoLentesLab +
        atualizado.custoOutros;

      atualizado.lucroBruto = atualizado.receitaLiquida - totalCPV;

      const totalDespesas =
        atualizado.comissoes +
        atualizado.taxasCartao +
        atualizado.folhaPagamento +
        atualizado.aluguel +
        atualizado.marketing +
        atualizado.despesasGerais +
        (atualizado.totalVariaveis || 0);

      atualizado.lucroLiquido = atualizado.lucroBruto - totalDespesas;
      atualizado.margemLucro =
        atualizado.receitaBruta > 0
          ? (atualizado.lucroLiquido / atualizado.receitaBruta) * 100
          : 0;

      return atualizado;
    });
  };

  // Input exclusivo para as Variáveis Personalizadas dentro do modal
  const handleVariavelModalChange = (id, valorStr) => {
    const num = valorStr === "" ? 0 : parseFloat(valorStr);
    setDadosMesEdit((prev) => {
      const newInputs = { ...prev.variaveisInputs, [id]: num };
      const newInputsRaw = { ...prev.variaveisInputsRaw, [id]: valorStr };

      let novaTotalVariaveis = 0;
      Object.values(newInputs).forEach((v) => (novaTotalVariaveis += v));

      const atualizado = {
        ...prev,
        variaveisInputs: newInputs,
        variaveisInputsRaw: newInputsRaw,
        totalVariaveis: novaTotalVariaveis,
      };

      const totalCPV =
        atualizado.custoArmacoes + atualizado.custoLentesLab + atualizado.custoOutros;
      atualizado.lucroBruto = atualizado.receitaLiquida - totalCPV;

      const totalDespesas =
        atualizado.comissoes +
        atualizado.taxasCartao +
        atualizado.folhaPagamento +
        atualizado.aluguel +
        atualizado.marketing +
        atualizado.despesasGerais +
        novaTotalVariaveis;

      atualizado.lucroLiquido = atualizado.lucroBruto - totalDespesas;
      atualizado.margemLucro =
        atualizado.receitaBruta > 0
          ? (atualizado.lucroLiquido / atualizado.receitaBruta) * 100
          : 0;

      return atualizado;
    });
  };

  const salvarDreMes = async () => {
    try {
      // 1. Atualiza visualmente o state das variáveis para a tabela refletir na hora
      setVariaveisMesValores((prev) => {
        const next = { ...prev };
        Object.entries(dadosMesEdit.variaveisInputs || {}).forEach(([vId, val]) => {
          if (!next[vId]) next[vId] = {};
          next[vId][dadosMesEdit.mesIndex + 1] = val;
        });
        return next;
      });

      // 2. Monta o Payload para a API
      const payload = {
        ano: Number(anoSelecionado),
        mes: dadosMesEdit.mesIndex + 1,
        loja: filtroLoja,
        financeiro: {
          impostos: Number(dadosMesEdit.impostos) || 0,
          custoArmacoes: Number(dadosMesEdit.custoArmacoes) || 0,
          custoLentesLab: Number(dadosMesEdit.custoLentesLab) || 0,
          custoOutros: Number(dadosMesEdit.custoOutros) || 0,
          comissoes: Number(dadosMesEdit.comissoes) || 0,
          taxasCartao: Number(dadosMesEdit.taxasCartao) || 0,
          folhaPagamento: Number(dadosMesEdit.folhaPagamento) || 0,
          aluguel: Number(dadosMesEdit.aluguel) || 0,
          marketing: Number(dadosMesEdit.marketing) || 0,
          despesasGerais: Number(dadosMesEdit.despesasGerais) || 0,
        },
        // Envia as variáveis alteradas no modal
        variaveisMes: dadosMesEdit.variaveisInputs || {},
      };

      const token = localStorage.getItem("vizumToken");
      const res = await fetch(`${API_BASE_URL}/dre/mes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setModalAberto(false);
        setRefreshTrigger((p) => p + 1);
      } else {
        alert("Falha ao sincronizar com o banco de dados.");
      }
    } catch (e) {
      console.error("Erro ao salvar DRE:", e);
    }
  };

  /* ----------------- CRUD DE VARIÁVEIS ----------------- */
  const salvarVariavel = async () => {
    const token = localStorage.getItem("vizumToken");
    const payload = {
      name: variavelEdit?.name?.trim() ?? "",
      value: 0, // <--- NOVA VARIÁVEL SEMPRE NASCE COM ZERO
      description: variavelEdit?.description?.trim() ?? "",
      year: Number(anoSelecionado),
      month: null, // Global
      store: filtroLoja,
    };

    try {
      const method = variavelEdit && variavelEdit._id ? "PUT" : "POST";
      const url =
        method === "POST"
          ? `${API_BASE_URL}/variables`
          : `${API_BASE_URL}/variables/${variavelEdit._id}`;

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Falha ao salvar variável");
      setRefreshTrigger((p) => p + 1);
      setModalVariavelAberto(false);
    } catch (e) {
      console.error(e);
      alert("Não foi possível salvar a variável.");
    }
  };

  const excluirVariavel = async (id) => {
    if (!window.confirm("Deseja realmente excluir esta variável?")) return;
    const token = localStorage.getItem("vizumToken");
    await fetch(`${API_BASE_URL}/variables/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    setRefreshTrigger((p) => p + 1);
  };

  /* ----------------- LOADER ----------------- */
  if (loading) {
    return (
      <div className="dre-loader-container">
        <div className="ghost-container">
          <svg className="ghost-animated" width="140" height="140" viewBox="0 0 100 100" fill="none">
            <path d="M20 50 C20 15, 80 15, 80 50 L80 85 C75 80, 70 90, 65 85 C60 80, 55 90, 50 85 C45 80, 40 90, 35 85 C30 80, 25 90, 20 85 Z" fill="#ffffff" stroke="#cbd5e1" strokeWidth="2" />
            <rect x="25" y="42" width="20" height="15" rx="4" fill="#0f172a" />
            <rect x="55" y="42" width="20" height="15" rx="4" fill="#0f172a" />
            <path d="M45 46 Q50 43 55 46" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M18 45 L25 45" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round" />
            <path d="M75 45 L82 45" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round" />
          </svg>
          <div className="ghost-shadow"></div>
        </div>
        <h3 className="loading-title">Contabilidade Sobrenatural...</h3>
        <p className="loading-subtitle">
          Nossos fantasminhas contadores estão sincronizando o DRE da ótica no além.
        </p>
        <style>{`
          .dre-loader-container { height:100vh; display:flex; flex-direction:column; justify-content:center; align-items:center; background:#f4f7fa; text-align:center; padding:20px; font-family:'Inter',sans-serif; }
          .ghost-container { position:relative; margin-bottom:24px; display:flex; flex-direction:column; align-items:center; justify-content:center; height:160px; }
          .ghost-animated { animation:floatGhost 2.5s ease-in-out infinite; filter:drop-shadow(0px 10px 15px rgba(0,0,0,.05)); }
          .ghost-shadow { width:70px; height:12px; background:rgba(15,23,42,.1); border-radius:50%; margin-top:20px; animation:scaleShadow 2.5s ease-in-out infinite; }
          .loading-title { margin:0; font-size:26px; font-weight:800; color:#0f172a; letter-spacing:-0.5px; }
          .loading-subtitle { margin:8px 0 0; font-size:15px; color:#64748b; font-weight:500; max-width:340px; line-height:1.5; }
          @keyframes floatGhost{0%,100%{transform:translateY(0px) rotate(0deg);} 50%{transform:translateY(-20px) rotate(3deg);}}
          @keyframes scaleShadow{0%,100%{transform:scale(1);opacity:0.2;} 50%{transform:scale(0.6);opacity:0.05;}}
        `}</style>
      </div>
    );
  }

  /* ----------------- RENDER ----------------- */
  return (
    <div className="dre-dashboard">
      <div className="topbar">
        <div className="brand-title">
          <h1>DRE Operacional</h1>
          <span>
            Demonstração de Resultados Baseado em Dados Reais – {anoSelecionado}
          </span>
        </div>
        <div className="controls-group">
          <div className="control-item">
            <label>Ano Exercício</label>
            <div className="select-wrapper">
              <select value={anoSelecionado} onChange={(e) => setAnoSelecionado(e.target.value)}>
                <option value="2026">2026</option>
                <option value="2025">2025</option>
              </select>
            </div>
          </div>
          <div className="control-item">
            <label>Unidade de Negócio</label>
            <div className="select-wrapper primary">
              <select value={filtroLoja} onChange={(e) => setFiltroLoja(e.target.value)}>
                <option value="Vizum 01">Loja: Vizum 01</option>
                <option value="Vizum 02">Loja: Vizum 02</option>
                <option value="Todas">📊 Consolidado (Rede)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="preset-filters">
        {["today", "week", "month", "lastMonth", "last7", "last30"].map((preset) => (
          <button
            key={preset}
            className={activePreset === preset ? "active" : ""}
            onClick={() => applyPreset(preset)}
          >
            {preset === "today" ? "Hoje" :
             preset === "week" ? "Esta Semana" :
             preset === "month" ? "Este Mês" :
             preset === "lastMonth" ? "Mês Passado" :
             preset === "last7" ? "Últimos 7 dias" : "Últimos 30 dias"}
          </button>
        ))}
      </div>

      <div className="date-range-display">
        Período: <strong>{dateInicio}</strong> → <strong>{dateFim}</strong>
      </div>

      {filtroLoja === "Todas" && (
        <div className="alert-banner warning">
          <strong>Modo de Leitura:</strong> Você está visualizando o resultado consolidado da rede. Para inserir despesas, selecione uma loja específica acima.
        </div>
      )}

      <div className="eligible-banner">
        🎯 Contratos elegíveis (valor  0): <strong>{totalEligibleContracts}</strong>
      </div>

      <div className="variables-section">
        <div className="variables-header">
          <h2>Variáveis ({anoSelecionado})</h2>
          {filtroLoja !== "Todas" && (
            <button
              className="btn-primary btn-add-var"
              onClick={() => {
                setVariavelEdit(null);
                setModalVariavelAberto(true);
              }}
            >
              ➕ Nova Variável
            </button>
          )}
        </div>

        {variaveis.length > 0 ? (
          <div className="card variables-card">
            <div className="table-scroll">
              <table className="ux-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Valor Base</th>
                    <th>Descrição</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {variaveis.map((v) => (
                    <tr key={v._id}>
                      <td>{v.name}</td>
                      <td>{formatCurrency(v.value)}</td>
                      <td>{v.description || "-"}</td>
                      <td>
                        <button className="btn-light btn-sm" onClick={() => {
                          setVariavelEdit(v);
                          setModalVariavelAberto(true);
                        }}>✏️ Editar</button>
                        <button className="btn-light btn-sm btn-danger" onClick={() => excluirVariavel(v._id)}>🗑️ Remover</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="no-variables">Nenhuma variável cadastrada para este período/loja.</p>
        )}
      </div>

      <div className="kpi-grid">
        <div className="kpi-box theme-blue">
          <div className="kpi-header">Vendas Totais (OS)</div>
          <div className="kpi-amount">{formatCurrency(kpiAno.receitaTotal)}</div>
        </div>
        <div className="kpi-box theme-orange">
          <div className="kpi-header">Custo com Produtos (CPV)</div>
          <div className="kpi-amount">{formatCurrency(kpiAno.custoProdutos)}</div>
        </div>
        <div className="kpi-box theme-purple">
          <div className="kpi-header">Despesas Operacionais</div>
          <div className="kpi-amount">{formatCurrency(kpiAno.despesasTotais)}</div>
        </div>
        <div className={`kpi-box ${kpiAno.lucroTotal >= 0 ? "theme-green" : "theme-red"}`}>
          <div className="kpi-header">Resultado Líquido (Lucro)</div>
          <div className="kpi-amount">{formatCurrency(kpiAno.lucroTotal)}</div>
          <div className="kpi-badge">{margemAnual.toFixed(1)}% Margem Real</div>
        </div>
      </div>

      <div className="card chart-card">
        <div className="card-header">
          <h2>Análise de Receita vs Lucratividade</h2>
          <span className="hint">Clique nas barras do gráfico para editar o mês</span>
        </div>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={meses} margin={{ top: 20, right: 20, bottom: 0, left: 20 }}>
              <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fill: "#64748b", fontSize: 13 }} dy={10} />
              <YAxis yAxisId="left" tickFormatter={(v) => `R$ ${v / 1000}k`} axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} dx={-10} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} />
              <Tooltip cursor={{ fill: "rgba(241, 245, 249, 0.4)" }} contentStyle={{ backgroundColor: "rgba(255,255,255,0.95)", backdropFilter: "blur(10px)", border: "1px solid #e2e8f0", borderRadius: "12px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }} formatter={(value, name) => [name === "Margem (%)" ? `${value.toFixed(2)}%` : formatCurrency(value), name]} />
              <Legend wrapperStyle={{ paddingTop: "20px", fontSize: "13px", fontWeight: 700, color: "#475569" }} iconType="circle" />
              <Bar yAxisId="left" dataKey="receitaBruta" name="Receita Bruta" fill="#3b82f6" radius={[6, 6, 0, 0]} maxBarSize={40} onClick={(d) => abrirDetalhesMes(d.payload)} className={filtroLoja !== "Todas" ? "cursor-pointer" : ""} />
              <Bar yAxisId="left" dataKey="lucroLiquido" name="Lucro Líquido" fill="#10b981" radius={[6, 6, 0, 0]} maxBarSize={40} onClick={(d) => abrirDetalhesMes(d.payload)} className={filtroLoja !== "Todas" ? "cursor-pointer" : ""} />
              <Line yAxisId="right" type="monotone" dataKey="margemLucro" name="Margem (%)" stroke="#f59e0b" strokeWidth={3} dot={{ r: 4, strokeWidth: 2, fill: "#fff" }} activeDot={{ r: 6 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card table-card">
        <div className="card-header border-bottom">
          <h2>Estrutura Contábil ({anoSelecionado})</h2>
          {filtroLoja !== "Todas" && (
            <span className="hint primary-hint">Aperte em ⚙️ no mês para realizar lançamentos</span>
          )}
        </div>
        <div className="table-scroll">
          <table className="ux-table">
            <thead>
              <tr>
                <th className="sticky-col head-col">Contas Contábeis</th>
                {meses.map((m) => (
                  <th key={m.mes} className={filtroLoja !== "Todas" ? "clickable-th" : ""} onClick={() => abrirDetalhesMes(m)}>
                    {m.mes} {filtroLoja !== "Todas" && <span className="edit-icon">⚙️</span>}
                  </th>
                ))}
                <th className="total-col">YTD (Acumulado)</th>
              </tr>
            </thead>
            <tbody>
              {/* ==== RECEITAS ==== */}
              <tr className="row-master text-primary">
                <td className="sticky-col">1. Receita Bruta (Vendas)</td>
                {meses.map((m) => (<td key={m.mes}>{formatCurrency(m.receitaBruta)}</td>))}
                <td className="total-col">{formatCurrency(kpiAno.receitaTotal)}</td>
              </tr>
              <tr className="row-detail">
                <td className="sticky-col depth-1">(-) Impostos s/ Vendas (DAS, ICMS)</td>
                {meses.map((m) => (<td key={m.mes} className="text-danger">{formatCurrency(m.impostos)}</td>))}
                <td className="total-col text-danger">{formatCurrency(meses.reduce((a, m) => a + m.impostos, 0))}</td>
              </tr>
              <tr className="row-subtotal">
                <td className="sticky-col">= Receita Operacional Líquida</td>
                {meses.map((m) => (<td key={m.mes}>{formatCurrency(m.receitaLiquida)}</td>))}
                <td className="total-col">{formatCurrency(meses.reduce((a, m) => a + m.receitaLiquida, 0))}</td>
              </tr>

              {/* ==== CPV ==== */}
              <tr className="row-section"><td colSpan={14}>Custo dos Produtos Vendidos (CPV)</td></tr>
              <tr className="row-detail">
                <td className="sticky-col depth-1">(-) Compra de Armações</td>
                {meses.map((m) => (<td key={m.mes} className="text-danger">{formatCurrency(m.custoArmacoes)}</td>))}
                <td className="total-col text-danger">{formatCurrency(meses.reduce((a, m) => a + m.custoArmacoes, 0))}</td>
              </tr>
              <tr className="row-detail">
                <td className="sticky-col depth-1">(-) Fatura Laboratório (Lentes)</td>
                {meses.map((m) => (<td key={m.mes} className="text-danger">{formatCurrency(m.custoLentesLab)}</td>))}
                <td className="total-col text-danger">{formatCurrency(meses.reduce((a, m) => a + m.custoLentesLab, 0))}</td>
              </tr>
              <tr className="row-detail">
                <td className="sticky-col depth-1">(-) Outros (L.C., Acessórios)</td>
                {meses.map((m) => (<td key={m.mes} className="text-danger">{formatCurrency(m.custoOutros)}</td>))}
                <td className="total-col text-danger">{formatCurrency(meses.reduce((a, m) => a + m.custoOutros, 0))}</td>
              </tr>
              <tr className="row-subtotal highlight">
                <td className="sticky-col">= Lucro Bruto</td>
                {meses.map((m) => (<td key={m.mes}>{formatCurrency(m.lucroBruto)}</td>))}
                <td className="total-col">{formatCurrency(meses.reduce((a, m) => a + m.lucroBruto, 0))}</td>
              </tr>

              {/* ==== DESPESAS ==== */}
              <tr className="row-section"><td colSpan={14}>Despesas Operacionais</td></tr>
              <tr className="row-detail">
                <td className="sticky-col depth-1">(-) Comissões de Vendas</td>
                {meses.map((m) => (<td key={m.mes} className="text-danger">{formatCurrency(m.comissoes)}</td>))}
                <td className="total-col text-danger">{formatCurrency(meses.reduce((a, m) => a + m.comissoes, 0))}</td>
              </tr>
              <tr className="row-detail">
                <td className="sticky-col depth-1">(-) Taxas Maquininha/Cartão</td>
                {meses.map((m) => (<td key={m.mes} className="text-danger">{formatCurrency(m.taxasCartao)}</td>))}
                <td className="total-col text-danger">{formatCurrency(meses.reduce((a, m) => a + m.taxasCartao, 0))}</td>
              </tr>
              <tr className="row-detail">
                <td className="sticky-col depth-1">(-) Folha Salarial e Encargos</td>
                {meses.map((m) => (<td key={m.mes} className="text-danger">{formatCurrency(m.folhaPagamento)}</td>))}
                <td className="total-col text-danger">{formatCurrency(meses.reduce((a, m) => a + m.folhaPagamento, 0))}</td>
              </tr>
              <tr className="row-detail">
                <td className="sticky-col depth-1">(-) Aluguel e IPTU</td>
                {meses.map((m) => (<td key={m.mes} className="text-danger">{formatCurrency(m.aluguel)}</td>))}
                <td className="total-col text-danger">{formatCurrency(meses.reduce((a, m) => a + m.aluguel, 0))}</td>
              </tr>
              <tr className="row-detail">
                <td className="sticky-col depth-1">(-) Marketing e Anúncios</td>
                {meses.map((m) => (<td key={m.mes} className="text-danger">{formatCurrency(m.marketing)}</td>))}
                <td className="total-col text-danger">{formatCurrency(meses.reduce((a, m) => a + m.marketing, 0))}</td>
              </tr>
              <tr className="row-detail">
                <td className="sticky-col depth-1">(-) Custos Gerais (Luz, Net, Sist)</td>
                {meses.map((m) => (<td key={m.mes} className="text-danger">{formatCurrency(m.despesasGerais)}</td>))}
                <td className="total-col text-danger">{formatCurrency(meses.reduce((a, m) => a + m.despesasGerais, 0))}</td>
              </tr>

              {/* ==== VARIÁVEIS GLOBAIS ==== */}
              {variaveis
                .filter((v) => v.isGlobal && v.year === Number(anoSelecionado) && v.store === filtroLoja)
                .map((v) => (
                  <tr key={v._id} className="row-detail">
                    <td className="sticky-col depth-1">(-) {v.name}</td>
                    {meses.map((m) => {
                      const val = variaveisMesValores[v._id]?.[m.mesIndex + 1] ?? v.value;
                      return (
                        <td key={m.mes} className="text-danger">
                          {formatCurrency(val)}
                        </td>
                      );
                    })}
                    <td className="total-col text-danger">
                      {formatCurrency(
                        meses.reduce((acc, m) => acc + (variaveisMesValores[v._id]?.[m.mesIndex + 1] ?? v.value), 0)
                      )}
                    </td>
                  </tr>
                ))}

              {/* ==== RESULTADO FINAL ==== */}
              <tr className="row-final">
                <td className="sticky-col">= Resultado Líquido do Mês</td>
                {meses.map((m) => (
                  <td key={m.mes} className={m.lucroLiquido >= 0 ? "text-success" : "text-danger"}>
                    {formatCurrency(m.lucroLiquido)}
                  </td>
                ))}
                <td className={`total-col ${kpiAno.lucroTotal >= 0 ? "text-success" : "text-danger"}`}>
                  {formatCurrency(kpiAno.lucroTotal)}
                </td>
              </tr>
              <tr className="row-margin">
                <td className="sticky-col">Margem de Lucratividade (%)</td>
                {meses.map((m) => (
                  <td key={m.mes} className={m.margemLucro >= 15 ? "text-success" : m.margemLucro >= 0 ? "text-warning" : "text-danger"}>
                    {m.margemLucro.toFixed(1)}%
                  </td>
                ))}
                <td className="total-col fw-bold">{margemAnual.toFixed(1)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ====================== MODAL DRE ====================== */}
      {modalAberto && dadosMesEdit && (
        <div className="ux-modal-overlay">
          <div className="ux-modal">
            <div className="ux-modal-header">
              <div className="header-info">
                <h2>Lançamento Contábil: {dadosMesEdit.mes} / {anoSelecionado}</h2>
                <span className="badge-loja">Unidade: {filtroLoja}</span>
              </div>
              <button className="btn-close" onClick={() => setModalAberto(false)}>&times;</button>
            </div>

            <div className="ux-modal-body">
              <div className="form-readonly-box">
                <label>Receita Bruta (sistema)</label>
                <h3>{formatCurrency(dadosMesEdit.receitaBruta)}</h3>
              </div>

              <div className="form-grid">
                <div className="form-block">
                  <h4>Deduções e Produto</h4>
                  <div className="ux-input-group"><label>Impostos s/ Vendas</label><input type="number" step="0.01" value={dadosMesEdit.impostosInput ?? dadosMesEdit.impostos} onChange={(e) => handleInputChange("impostos", e.target.value)} /></div>
                  <div className="ux-input-group"><label>Fatura Laboratório</label><input type="number" step="0.01" value={dadosMesEdit.custoLentesLabInput ?? dadosMesEdit.custoLentesLab} onChange={(e) => handleInputChange("custoLentesLab", e.target.value)} /></div>
                  <div className="ux-input-group"><label>Compra Armações</label><input type="number" step="0.01" value={dadosMesEdit.custoArmacoesInput ?? dadosMesEdit.custoArmacoes} onChange={(e) => handleInputChange("custoArmacoes", e.target.value)} /></div>
                  <div className="ux-input-group"><label>Outros (LC/Acessórios)</label><input type="number" step="0.01" value={dadosMesEdit.custoOutrosInput ?? dadosMesEdit.custoOutros} onChange={(e) => handleInputChange("custoOutros", e.target.value)} /></div>
                </div>

                <div className="form-block">
                  <h4>Custos Operacionais</h4>
                  <div className="ux-input-group"><label>Comissões Equipe</label><input type="number" step="0.01" value={dadosMesEdit.comissoesInput ?? dadosMesEdit.comissoes} onChange={(e) => handleInputChange("comissoes", e.target.value)} /></div>
                  <div className="ux-input-group"><label>Taxas Cartão</label><input type="number" step="0.01" value={dadosMesEdit.taxasCartaoInput ?? dadosMesEdit.taxasCartao} onChange={(e) => handleInputChange("taxasCartao", e.target.value)} /></div>
                  <div className="ux-input-group"><label>Folha e Encargos</label><input type="number" step="0.01" value={dadosMesEdit.folhaPagamentoInput ?? dadosMesEdit.folhaPagamento} onChange={(e) => handleInputChange("folhaPagamento", e.target.value)} /></div>
                  <div className="ux-input-group"><label>Aluguel da Loja</label><input type="number" step="0.01" value={dadosMesEdit.aluguelInput ?? dadosMesEdit.aluguel} onChange={(e) => handleInputChange("aluguel", e.target.value)} /></div>
                  <div className="ux-input-group"><label>Marketing</label><input type="number" step="0.01" value={dadosMesEdit.marketingInput ?? dadosMesEdit.marketing} onChange={(e) => handleInputChange("marketing", e.target.value)} /></div>
                  <div className="ux-input-group"><label>Despesas Gerais</label><input type="number" step="0.01" value={dadosMesEdit.despesasGeraisInput ?? dadosMesEdit.despesasGerais} onChange={(e) => handleInputChange("despesasGerais", e.target.value)} /></div>
                </div>

                {/* --- SEÇÃO DE VARIÁVEIS EXCLUSIVA DO MODAL --- */}
                {variaveis.filter((v) => v.isGlobal && v.year === Number(anoSelecionado) && v.store === filtroLoja).length > 0 && (
                  <div className="form-block">
                    <h4>Variáveis Personalizadas</h4>
                    {variaveis
                      .filter((v) => v.isGlobal && v.year === Number(anoSelecionado) && v.store === filtroLoja)
                      .map((v) => (
                        <div className="ux-input-group" key={v._id}>
                          <label>{v.name}</label>
                          <input
                            type="number"
                            step="0.01"
                            value={dadosMesEdit.variaveisInputsRaw?.[v._id] ?? ""}
                            onChange={(e) => handleVariavelModalChange(v._id, e.target.value)}
                          />
                        </div>
                      ))}
                  </div>
                )}
              </div>

              <div className={`dynamic-result ${dadosMesEdit.lucroLiquido >= 0 ? "bg-success" : "bg-danger"}`}>
                <div className="res-item">
                  <span>Projeção Lucro Líquido:</span>
                  <h3>{formatCurrency(dadosMesEdit.lucroLiquido)}</h3>
                </div>
                <div className="res-item text-right">
                  <span>Margem:</span>
                  <h3>{dadosMesEdit.margemLucro.toFixed(2)}%</h3>
                </div>
              </div>
            </div>

            <div className="ux-modal-footer">
              <button className="btn-light" onClick={() => setModalAberto(false)}>Cancelar</button>
              <button className="btn-primary" onClick={salvarDreMes}>Salvar Lançamentos</button>
            </div>
          </div>
        </div>
      )}

      {/* ====================== MODAL VARIÁVEL ====================== */}
      {modalVariavelAberto && (
        <div className="ux-modal-overlay">
          <div className="ux-modal">
            <div className="ux-modal-header">
              <h2>{variavelEdit ? "Editar" : "Nova"} Variável – {anoSelecionado}/{filtroLoja}</h2>
              <button className="btn-close" onClick={() => setModalVariavelAberto(false)}>&times;</button>
            </div>

            <div className="ux-modal-body">
              <div className="ux-input-group">
                <label>Nome da Variável</label>
                <input type="text" value={variavelEdit?.name ?? ""} onChange={(e) => setVariavelEdit((prev) => ({ ...(prev || {}), name: e.target.value }))} placeholder="ex.: Taxa de Água" />
              </div>

              {/* O Campo 'Valor' foi REMOVIDO na criação. Se for editar, podemos mostrar ou não. 
                  Conforme seu pedido: 'na hora de cria nova varivale tira o cmapo de vlor deixa oculto msm' */}
              {variavelEdit && (
                <div className="ux-input-group">
                  <label>Valor Base Padrão</label>
                  <input type="number" step="0.01" value={variavelEdit?.value ?? 0} onChange={(e) => setVariavelEdit((prev) => ({ ...(prev || {}), value: e.target.value }))} />
                </div>
              )}

              <div className="ux-input-group">
                <label>Descrição (opcional)</label>
                <input type="text" value={variavelEdit?.description ?? ""} onChange={(e) => setVariavelEdit((prev) => ({ ...(prev || {}), description: e.target.value }))} />
              </div>
            </div>

            <div className="ux-modal-footer">
              <button className="btn-light" onClick={() => setModalVariavelAberto(false)}>Cancelar</button>
              <button className="btn-primary" onClick={salvarVariavel}>Salvar Variável</button>
            </div>
          </div>
        </div>
      )}

      {/* ====================== ESTILOS (CSS) ====================== */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing:border-box; }
        .dre-dashboard { padding:32px; background:#f4f7fa; min-height:100vh; font-family:'Inter',sans-serif; color:#0f172a; }
        .topbar { display:flex; justify-content:space-between; align-items:center; margin-bottom:24px; flex-wrap:wrap; gap:20px; }
        .brand-title h1 { margin:0; font-size:28px; font-weight:800; color:#0f172a; letter-spacing:-0.5px; }
        .brand-title span { font-size:14px; color:#64748b; font-weight:500; }
        .controls-group { display:flex; gap:16px; }
        .control-item { display:flex; flex-direction:column; gap:6px; }
        .control-item label { font-size:11px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.5px; }
        .select-wrapper select { appearance:none; background:#fff; border:1px solid #cbd5e1; padding:10px 36px 10px 16px; border-radius:10px; font-size:14px; font-weight:600; color:#1e293b; cursor:pointer; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 12px center; }
        .select-wrapper.primary select { border-color:#3b82f6; background:#eff6ff; color:#1d4ed8; }
        .select-wrapper select:focus { outline:none; border-color:#2563eb; box-shadow:0 0 0 3px rgba(59,130,246,.15); }
        .alert-banner { padding:12px 16px; border-radius:8px; margin-bottom:24px; font-size:14px; display:flex; align-items:center; }
        .alert-banner.warning { background:#fffbeb; color:#b45309; border:1px solid #fde68a; }
        .preset-filters { display:flex; flex-wrap:wrap; gap:8px; margin:12px 0; }
        .preset-filters button { background:#e2e8f0; border:none; border-radius:8px; padding:6px 12px; font-weight:600; font-size:13px; cursor:pointer; transition:background .2s; }
        .preset-filters button:hover { background:#cbd5e1; }
        .preset-filters button.active { background:#3b82f6; color:#fff; }
        .date-range-display { font-size:13px; color:#475569; margin-bottom:8px; }
        .eligible-banner { background:#e0f2fe; color:#0369a1; padding:8px 16px; border-radius:12px; font-weight:600; display:inline-flex; align-items:center; gap:6px; margin-bottom:20px; }
        .variables-section { margin-top:24px; }
        .variables-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; }
        .variables-header h2 { margin:0; font-size:18px; font-weight:700; color:#0f172a; }
        .btn-add-var { padding:6px 12px; background:#2563eb; border:none; border-radius:8px; color:#fff; font-weight:600; cursor:pointer; }
        .btn-add-var:hover { background:#1d4ed8; }
        .no-variables { font-size:14px; color:#64748b; margin-top:8px; }
        .kpi-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(240px,1fr)); gap:20px; margin-bottom:24px; }
        .kpi-box { background:#fff; border:1px solid #e2e8f0; border-radius:16px; padding:24px; box-shadow:0 4px 6px -1px rgba(0,0,0,.02), 0 10px 15px -3px rgba(0,0,0,.02); position:relative; overflow:hidden; }
        .kpi-header { font-size:13px; font-weight:600; color:#64748b; margin-bottom:8px; }
        .kpi-amount { font-size:28px; font-weight:800; color:#0f172a; letter-spacing:-0.5px; }
        .kpi-badge { margin-top:12px; padding:4px 8px; border-radius:6px; font-size:12px; font-weight:700; background:rgba(255,255,255,.8); display:inline-block; }
        .theme-blue { border-top:4px solid #3b82f6; }
        .theme-orange { border-top:4px solid #f97316; }
        .theme-purple { border-top:4px solid #8b5cf6; }
        .theme-green { background:linear-gradient(135deg,#10b981 0%,#059669 100%); border:none; }
        .theme-red { background:linear-gradient(135deg,#ef4444 0%,#dc2626 100%); border:none; }
        .theme-green .kpi-header, .theme-red .kpi-header { color:rgba(255,255,255,.8); }
        .theme-green .kpi-amount, .theme-red .kpi-amount { color:#fff; }
        .theme-green .kpi-badge { color:#065f46; }
        .theme-red .kpi-badge { color:#991b1b; }
        .card { background:#fff; border-radius:16px; box-shadow:0 1px 3px rgba(0,0,0,.05); border:1px solid #e2e8f0; margin-bottom:24px; overflow:hidden; }
        .card-header { padding:20px 24px; display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid #f1f5f9; }
        .card-header h2 { margin:0; font-size:18px; font-weight:700; color:#1e293b; }
        .hint { font-size:12px; color:#64748b; background:#f8fafc; padding:6px 12px; border-radius:20px; }
        .primary-hint { background:#eff6ff; color:#2563eb; font-weight:600; }
        .chart-container { padding:0 20px 20px 0; }
        .cursor-pointer { cursor:pointer; }
        .ux-table { width:100%; border-collapse:collapse; min-width:1100px; font-variant-numeric:tabular-nums; }
        .ux-table th, .ux-table td { padding:14px 16px; text-align:right; border-bottom:1px solid #f1f5f9; font-size:14px; white-space:nowrap; }
        .ux-table th:first-child, .ux-table td:first-child { text-align:left; }
        .ux-table thead th { background:#f8fafc; font-size:12px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:.5px; border-bottom:2px solid #e2e8f0; }
        .sticky-col { position:sticky; left:0; background:#fff; z-index:2; box-shadow:inset -1px 0 0 #e2e8f0; }
        .head-col { background:#f8fafc !important; z-index:3; }
        .total-col { background:#f8fafc; font-weight:700; box-shadow:inset 1px 0 0 #e2e8f0; }
        .row-master { background:#fafafa; font-weight:700; }
        .row-section { background:#fff; font-size:11px; font-weight:800; color:#94a3b8; text-transform:uppercase; padding-top:24px; padding-bottom:8px; border:none; letter-spacing:1px; }
        .row-detail:hover td { background:#f8fafc; }
        .depth-1 { padding-left:24px !important; color:#64748b; font-size:13px; }
        .row-subtotal { font-weight:600; color:#334155; border-top:1px dashed #cbd5e1; }
        .row-subtotal.highlight { background:#fafafa; }
        .row-final { font-weight:800; font-size:15px; background:#f8fafc; border-top:2px solid #cbd5e1; }
        .row-margin { font-weight:600; font-size:13px; border-bottom:none; }
        .text-primary { color:#2563eb !important; }
        .text-success { color:#059669 !important; }
        .text-danger { color:#dc2626 !important; }
        .text-warning { color:#d97706 !important; }
        .fw-bold { font-weight:800 !important; }
        .clickable-th { cursor:pointer; transition:.2s; position:relative; }
        .clickable-th:hover { background:#e2e8f0; color:#2563eb; }
        .edit-icon { opacity:.3; font-size:10px; margin-left:4px; transition:.2s; }
        .clickable-th:hover .edit-icon { opacity:1; }

        .ux-modal-overlay { position:fixed; inset:0; background:rgba(15,23,42,.6); backdrop-filter:blur(4px); display:flex; justify-content:center; align-items:center; z-index:9999; padding:20px; }
        .ux-modal { background:#fff; width:100%; max-width:600px; border-radius:20px; box-shadow:0 25px 50px -12px rgba(0,0,0,.25); display:flex; flex-direction:column; max-height:90vh; animation:modalFade .3s ease-out; }
        @keyframes modalFade { from { opacity:0; transform:translateY(20px) scale(.98); } to { opacity:1; transform:translateY(0) scale(1); } }
        .ux-modal-header { padding:24px; border-bottom:1px solid #f1f5f9; display:flex; justify-content:space-between; align-items:flex-start; }
        .ux-modal-header h2 { margin:0 0 8px 0; font-size:20px; font-weight:800; color:#0f172a; }
        .badge-loja { background:#eff6ff; color:#1d4ed8; padding:4px 10px; border-radius:6px; font-size:12px; font-weight:700; display:inline-block; }
        .btn-close { background:transparent; border:none; font-size:28px; line-height:1; color:#94a3b8; cursor:pointer; }
        .btn-close:hover { color:#0f172a; }
        .ux-modal-body { padding:24px; overflow-y:auto; display:flex; flex-direction:column; gap:24px; }
        .ux-modal-body::-webkit-scrollbar { width:6px; }
        .ux-modal-body::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:10px; }
        .form-readonly-box { background:#f8fafc; padding:16px; border-radius:12px; border:1px solid #e2e8f0; text-align:center; }
        .form-readonly-box label { display:block; font-size:12px; color:#64748b; font-weight:600; margin-bottom:4px; }
        .form-readonly-box h3 { margin:0; font-size:24px; color:#3b82f6; font-weight:800; }
        .form-grid { display:flex; flex-direction:column; gap:24px; }
        .form-block h4 { margin:0 0 16px 0; font-size:14px; color:#0f172a; border-bottom:2px solid #f1f5f9; padding-bottom:8px; }
        .ux-input-group { display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; }
        .ux-input-group label { font-size:13px; color:#475569; font-weight:500; }
        .ux-input-group input { width:150px; padding:10px 12px; border:1px solid #cbd5e1; border-radius:8px; font-family:'Inter'; font-size:14px; text-align:right; font-weight:600; color:#0f172a; transition:.2s; }
        .ux-input-group input:focus { outline:none; border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.1); }
        .dynamic-result { padding:20px; border-radius:12px; display:flex; justify-content:space-between; color:#fff; }
        .bg-success { background:linear-gradient(135deg,#10b981,#059669); }
        .bg-danger { background:linear-gradient(135deg,#ef4444,#dc2626); }
        .res-item span { display:block; font-size:12px; opacity:.9; font-weight:600; margin-bottom:4px; text-transform:uppercase; }
        .res-item h3 { margin:0; font-size:24px; font-weight:800; }
        .text-right { text-align:right; }
        .ux-modal-footer { padding:20px 24px; border-top:1px solid #f1f5f9; display:flex; justify-content:flex-end; gap:12px; }
        .btn-light { padding:10px 16px; background:#fff; border:1px solid #cbd5e1; border-radius:8px; font-weight:600; color:#475569; cursor:pointer; transition:.2s; }
        .btn-light:hover { background:#f8fafc; color:#0f172a; }
        .btn-primary { padding:10px 18px; background:#2563eb; border:none; border-radius:8px; font-weight:600; color:#fff; cursor:pointer; transition:.2s; box-shadow:0 4px 6px -1px rgba(37,99,235,.2); }
        .btn-primary:hover { background:#1d4ed8; }
        .btn-sm { padding:6px 10px; font-size:12px; margin-right:6px; }
        .btn-danger { color:#dc2626; border-color:#fca5a5; }
        .btn-danger:hover { background:#fef2f2; color:#b91c1c; }

        @media (max-width:768px) {
          .dre-dashboard { padding:20px; }
          .kpi-grid { grid-template-columns:1fr; gap:20px; }
          .topbar { flex-direction:column; align-items:flex-start; }
          .kpi-amount { font-size:38px; }
          .card { padding:20px; }
        }
      `}</style>
    </div>
  );
}
